import React, { Fragment, useContext } from 'react';
import { Grid, Link, Paper, Typography, useTheme } from '@material-ui/core';
import Delete from '@material-ui/icons/Delete';
import { TextField } from '@michelin/acid-components';
import { useTranslation } from '@michelin/central-provider';
import QtySelector from 'components/QtySelector/qtySelector';
import { useCurrentLocationIsDealer } from 'hooks/useCurrentLocationIsDealer';
import { AdditionalServicesType, UFOPart } from 'types/UFOTypes';
import { getMspn } from 'utils/getMspn';
import { CommonPricesParamsType } from 'utils/getPricesCallCommonParams';
import { SelectedServicesType, WizardContext } from '../WizardContext';
import Pricing from './Pricing';

export const WizardAdditionalServicesDesktop = (props: {
  additionalServices: AdditionalServicesType[];
  searchedPart?: AdditionalServicesType | UFOPart;
  refs?;
  highlight?: boolean;
  category?: string;
  setSelectedServices: React.Dispatch<React.SetStateAction<SelectedServicesType[]>>;
  selectedServices: SelectedServicesType[];
  commonPricesParams: CommonPricesParamsType;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    additionalServices,
    searchedPart,
    refs,
    highlight,
    category,
    selectedServices,
    setSelectedServices,
    commonPricesParams,
  } = props;
  const { customReq, setCustomReq, setPql, getPql } = useContext(WizardContext);
  const isDealer = useCurrentLocationIsDealer();
  const options: { label: string; value: number }[] = [];
  for (let i = 1; i <= 8; i += 1) {
    options.push({ label: t('Qty: {{i}}', { i }), value: i });
  }

  const priceRender = (service: AdditionalServicesType, line) => (
    <Pricing
      setPql={(price: number) => {
        if (setPql) setPql(getMspn(service) as string, price, line.index as number, '');
        else {
          setSelectedServices(
            selectedServices?.map((s) => {
              if (s.part.mspn === getMspn(service)) {
                const { lines, part } = s;
                lines[line.index].pql = price;
                return { part, lines };
              }
              return s;
            }),
          );
        }
      }}
      qty={line.quantity || 1}
      setQty={(qty) =>
        setSelectedServices(
          selectedServices?.map((s) => {
            if (s.part.mspn === getMspn(service)) {
              const { lines, part } = s;
              lines[line.index].quantity = qty || 1;
              return { part, lines };
            }
            return s;
          }),
        )
      }
      pqlPrice={getPql ? getPql(getMspn(service) as string, line.index as number, '') || line.pql : line.pql}
      service={service}
      line={line.index}
      setSelectedServices={setSelectedServices}
      selectedServices={selectedServices}
      commonPricesParams={commonPricesParams}
      stockingOrder
    />
  );

  const filteredServices = additionalServices.filter(
    (service: AdditionalServicesType) => service.additional_information?.category === category || !category,
  );
  return (
    <ul style={{ listStyleType: 'none', padding: 0 }}>
      <Grid container spacing={0} style={{ margin: 0, padding: 0 }}>
        {filteredServices.map((service: AdditionalServicesType, i) => {
          const lastItem = i === filteredServices.length - 1;
          const multipleLines = service.additional_information?.multi_lines === 'Y';
          return (
            <Grid item xs={12} md={multipleLines ? 12 : 6} style={{ margin: 0, padding: 0 }}>
              <li key={service.mspn} ref={refs ? refs[service.mspn] : undefined}>
                <Paper
                  elevation={2}
                  style={{
                    padding: 16,
                    margin: 8,
                    marginBottom: lastItem ? 56 : undefined,
                    background: searchedPart?.mspn === service.mspn && highlight ? theme.palette.info.light : undefined,
                  }}
                  id="partPaper"
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={multipleLines ? 12 : 5}>
                      <Typography style={{ fontWeight: 'bold', fontSize: 16 }}>{t(service.description)}</Typography>
                      <Typography color="textSecondary" id="servicePartNumber">
                        {t('Part #: {{partNumber}}', { partNumber: service.mspn })}
                        {service.dealer_part_id && <>{`|${service.dealer_part_id}`}</>}
                      </Typography>
                      {multipleLines && (
                        // TODO: consider Button over Link?
                        <Link
                          style={{ cursor: 'pointer' }}
                          onClick={() =>
                            setSelectedServices(
                              selectedServices.map((s) => {
                                if (s.part.mspn === service.mspn) {
                                  const { lines, part } = s;
                                  lines.push({
                                    selected: false,
                                    quantity: 1,
                                    id: null,
                                    index: lines.length,
                                    comment: '',
                                  });
                                  return { part, lines };
                                }
                                return s;
                              }),
                            )
                          }
                        >
                          {t('+ Add new line item')}
                        </Link>
                      )}
                    </Grid>
                    {selectedServices?.length &&
                      selectedServices
                        ?.find((ss) => ss.part.mspn === getMspn(service))
                        ?.lines.filter((l) => !!l.quantity)
                        .map((l) => (
                          <Fragment key={`laborNameTextField${getMspn(service) as string}${l.index}`}>
                            {multipleLines && (
                              <Grid item xs={12} md={7} style={{ display: 'flex', alignItems: 'center' }}>
                                <TextField
                                  key={`laborName${getMspn(service) as string}${l.index}`}
                                  id={`laborName${getMspn(service) as string}${l.index}`} // ID requested byQA
                                  onChange={(e) =>
                                    setSelectedServices(
                                      selectedServices.map((s) => {
                                        if (s.part.mspn === getMspn(service)) {
                                          const { lines, part } = s;
                                          lines[l.index].comment = e.target.value;
                                          return { part, lines };
                                        }
                                        return s;
                                      }),
                                    )
                                  }
                                  placeholder={t('Type in a part, service or labor name')}
                                  value={l.comment}
                                />
                                {l.index > 0 && (
                                  <Link
                                    style={{ cursor: 'pointer' }}
                                    onClick={() =>
                                      setSelectedServices(
                                        selectedServices?.map((s) => {
                                          if (s.part.mspn === service.mspn) {
                                            const { lines, part } = s;
                                            lines[l.index].quantity = 0;
                                            return { part, lines };
                                          }
                                          return s;
                                        }),
                                      )
                                    }
                                  >
                                    <Delete style={{ margin: 8, marginBottom: -16 }} />
                                  </Link>
                                )}
                              </Grid>
                            )}
                            <Grid item xs={12} md={multipleLines ? 5 : 7}>
                              {priceRender(service, l)}
                            </Grid>
                          </Fragment>
                        ))}
                  </Grid>
                </Paper>
              </li>
            </Grid>
          );
        })}
        {customReq && !isDealer && category === 'MISC' && (
          <Grid item xs={12} style={{ margin: 0, padding: 0, height: 180 }}>
            <Paper
              elevation={2}
              style={{
                padding: 16,
                margin: 8,
              }}
              id="partPaper"
            >
              <Grid container spacing={2}>
                <Grid item xs={9}>
                  <TextField
                    onChange={(e: any) => {
                      setCustomReq({ ...customReq, text: e.target.value });
                    }}
                    placeholder={t('Please describe your need')}
                    value={customReq.text}
                    label={t('Request a Quote')}
                    rows={4}
                    multiline
                  />
                </Grid>
                <Grid xs={3} item container direction="row" alignItems="center" justify="flex-end">
                  <div style={{ marginTop: 16 }}>
                    <QtySelector
                      value={1}
                      onAdd={() => setCustomReq({ ...customReq, selected: true })}
                      onRemove={() => setCustomReq({ ...customReq, selected: false })}
                      maxValue={1}
                      defaultValue={1}
                      onChange={() => {}}
                      selected={customReq.selected}
                    />
                  </div>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}
      </Grid>
    </ul>
  );
};
